'use client';

import { useFormik } from 'formik';
import { signIn } from 'next-auth/react';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { GoEyeClosed } from 'react-icons/go';
import { IoEyeOutline } from 'react-icons/io5';
import * as Yup from 'yup';

import { passwordRegex } from '@/constants/regex.constants';
import { RESPONSE_MESSAGES } from '@/constants/responseMessages';
import { useTaost } from '@/hooks/useToast';
import { forgotPassword, registerUser, signInUser } from '@/services/AuthService';
import MediaButton from '../MediaButton';
import Button from '../ui/Button';
import Input from '../ui/Form/Input';

type RegisterProps = {
  isOpen: boolean;
  onClose: () => void;
  openLogin: () => void;
  openRegister: () => void;
  isLoginMode: boolean;
};

const Register: React.FC<RegisterProps> = ({ isOpen, onClose, openLogin, openRegister, isLoginMode }) => {
  const [visible, setVisible] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [transitioning, setTransitioning] = useState<boolean>(false);

  const [passwordVisible, setPasswordVisible] = useState<boolean>(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState<boolean>(false);
  const [forgotPasswordMode, setForgotPasswordMode] = useState<boolean>(false);
  const { successToast, errorToast } = useTaost();

  const t = useTranslations('sign_in');
  const router = useRouter();

  const loginFormik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: Yup.object({
      email: Yup.string().email('Invalid email address').required('Email is required'),
      password: Yup.string()
        .matches(passwordRegex.length, RESPONSE_MESSAGES.PASSWORD_REQUIREMENTS)
        .matches(passwordRegex.lowercase, RESPONSE_MESSAGES.PASSWORD_REQUIREMENTS)
        .matches(passwordRegex.uppercase, RESPONSE_MESSAGES.PASSWORD_REQUIREMENTS)
        .matches(passwordRegex.number, RESPONSE_MESSAGES.PASSWORD_REQUIREMENTS)
        .matches(passwordRegex.specialChar, RESPONSE_MESSAGES.PASSWORD_REQUIREMENTS)
        .required(RESPONSE_MESSAGES.PASSWORD_REQUIRED),
    }),
    validateOnBlur: true,
    validateOnChange: false,
    onSubmit: async values => {
      setLoading(true);
      try {
        const response = await signInUser(values);
        if (response?.status) {
          setLoading(false);

          router.push('/dashboard');
          successToast(response?.message || RESPONSE_MESSAGES.LOGIN_SUCCESS);
        } else {
          errorToast(response?.message || RESPONSE_MESSAGES.USER_NOT_FOUND);
          setLoading(false);
        }
        loginFormik.resetForm();
        router.push('/');
        onClose();

        await signIn('credentials', {
          ...response.data,
          redirect: true,
        });
      } catch (err: any) {
        errorToast(err?.response?.data?.message || RESPONSE_MESSAGES.USER_NOT_FOUND);
      } finally {
        setLoading(false);
      }
    },
  });

  const registerFormik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      phone: '',
      email: '',
      password: '',
      confirmPassword: '',
    },
    validationSchema: Yup.object({
      firstName: Yup.string().required('First name is required'),
      lastName: Yup.string().required('Last name is required'),
      phone: Yup.string().required('Phone is required'),
      email: Yup.string().email('Invalid email address').required('Email is required'),
      password: Yup.string()
        .matches(passwordRegex.length, RESPONSE_MESSAGES.PASSWORD_REQUIREMENTS)
        .matches(passwordRegex.lowercase, RESPONSE_MESSAGES.PASSWORD_REQUIREMENTS)
        .matches(passwordRegex.uppercase, RESPONSE_MESSAGES.PASSWORD_REQUIREMENTS)
        .matches(passwordRegex.number, RESPONSE_MESSAGES.PASSWORD_REQUIREMENTS)
        .matches(passwordRegex.specialChar, RESPONSE_MESSAGES.PASSWORD_REQUIREMENTS)
        .required(RESPONSE_MESSAGES.PASSWORD_REQUIRED),
      confirmPassword: Yup.string().oneOf([Yup.ref('password')], RESPONSE_MESSAGES.PASSWORD_MISMATCH),
    }),
    validateOnBlur: true,
    validateOnChange: false,
    onSubmit: async values => {
      setLoading(true);
      try {
        await registerUser(values);
        const res = await signIn('credentials', {
          ...values,
          redirect: false,
        });
        if (res && res.ok) {
          setLoading(false);
          successToast(RESPONSE_MESSAGES.REGISTRATION_SUCCESS);
          router.push('/');
        } else {
          setLoading(false);
        }

        registerFormik.resetForm();
        onClose();
      } catch (err: any) {
        errorToast(RESPONSE_MESSAGES.REGISTRATION_FAILED || err.message);
        console.error('Registration error:', err);
      } finally {
        setLoading(false);
      }
    },
  });

  const forgotPasswordFormik = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema: Yup.object({
      email: Yup.string().email('Invalid email address').required('Email is required'),
    }),
    validateOnBlur: true,
    validateOnChange: false,
    onSubmit: async values => {
      setLoading(true);
      try {
        await forgotPassword(values);
        setForgotPasswordMode(false);
        onClose();
      } catch (err) {
        toast.error(RESPONSE_MESSAGES.FORGOT_PASSWORD_FAILED);
        console.error('Forgot Password failed:', err);
      } finally {
        setLoading(false);
      }
    },
  });

  const togglePasswordVisibility = () => {
    setPasswordVisible(prev => !prev);
  };
  const toggleConfirmPasswordVisibility = () => {
    setConfirmPasswordVisible(prev => !prev);
  };

  useEffect(() => {
    if (isOpen) {
      setVisible(true);
      setTimeout(() => setTransitioning(true), 300);
    } else {
      setTransitioning(false);
      setTimeout(() => setVisible(false), 300);
    }
  }, [isOpen]);

  if (!visible) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex h-full min-h-screen justify-end bg-black bg-opacity-50 transition-opacity duration-300 ${
        transitioning ? 'opacity-100' : 'opacity-0'
      }`}
      onClick={onClose}
    >
      <div
        className={`ease-[cubic-bezier(0.25, 1, 0.5, 1)] flex h-full w-full max-w-md transform flex-col bg-white shadow-lg transition-transform duration-500 dark:border-l dark:border-clr-2e dark:bg-black dark:bg-gradient-to-l dark:from-[#121212] dark:via-[#22242bc9] ${
          transitioning ? 'translate-x-0' : 'translate-x-full'
        }`}
        onClick={e => e.stopPropagation()}
      >
        <button onClick={onClose} className="absolute right-4 top-4 text-xl text-gray-500 hover:text-gray-700">
          &times;
        </button>

        <div className="mt-12 overflow-y-auto p-6">
          {' '}
          {isLoginMode && !forgotPasswordMode && (
            <>
              <h2 className="mb-2 text-[30px] font-bold text-clr-2e dark:text-white">{t('sign_in')} </h2>

              <button
                onClick={openRegister}
                className="mb-12 mt-3 text-base font-bold text-clr-0a dark:text-white"
                dangerouslySetInnerHTML={{ __html: t.raw('sign_in_havent_account') }}
              />

              <form onSubmit={loginFormik.handleSubmit} className="flex flex-col gap-12">
                <Input
                  type="text"
                  placeholder={t('sign_in_email')}
                  inputName="email"
                  value={loginFormik.values.email}
                  onChange={loginFormik.handleChange}
                  error={loginFormik.errors.email}
                />
                <Input
                  type={passwordVisible ? 'text' : 'password'}
                  placeholder={t('sign_in_password')}
                  inputName="password"
                  value={loginFormik.values.password}
                  onChange={loginFormik.handleChange}
                  error={loginFormik.errors.password}
                  icon={
                    passwordVisible ? (
                      <GoEyeClosed onClick={togglePasswordVisibility} className="cursor-pointer" />
                    ) : (
                      <IoEyeOutline onClick={togglePasswordVisibility} className="cursor-pointer" />
                    )
                  }
                />
                <Button name={t('sign_in')} loading={loading} type="submit" />
                <p
                  className="mt-4 cursor-pointer text-sm text-clr-20 dark:text-clr-41"
                  onClick={() => setForgotPasswordMode(true)}
                >
                  {t('sign_in_forgot_password')}
                </p>
              </form>
              <MediaButton />
            </>
          )}
          {forgotPasswordMode && (
            <>
              <h2 className="mb-2 text-[30px] font-bold text-clr-2e dark:text-white">{t('sign_in_forgot_password')}</h2>

              <button
                onClick={() => setForgotPasswordMode(false)}
                className="mb-12 mt-3 text-base font-bold text-clr-0a dark:text-white"
                dangerouslySetInnerHTML={{ __html: t.raw('sign_in_back_to_sign_in') }}
              />

              <form onSubmit={forgotPasswordFormik.handleSubmit} className="flex flex-col gap-12">
                <Input
                  type="text"
                  placeholder={t('sign_in_email')}
                  inputName="email"
                  value={forgotPasswordFormik.values.email}
                  onChange={forgotPasswordFormik.handleChange}
                  error={forgotPasswordFormik.errors.email}
                />
                <Button name={t('sign_in_forgot_password_reset_btn')} loading={loading} type="submit" />
              </form>
            </>
          )}
          {!isLoginMode && (
            <>
              <h2 className="mb-2 text-[30px] font-bold text-clr-2e dark:text-white">{t('sign_in_create_account')}</h2>

              <button
                onClick={openLogin}
                className="mb-12 mt-3 text-base font-bold text-clr-0a dark:text-white"
                dangerouslySetInnerHTML={{ __html: t.raw('sign_in_have_account') }}
              />

              <form onSubmit={registerFormik.handleSubmit} className="flex flex-col gap-9">
                <Input
                  type="text"
                  placeholder={t('sign_up_first_name')}
                  inputName="firstName"
                  value={registerFormik.values.firstName}
                  onChange={registerFormik.handleChange}
                  error={registerFormik.errors.firstName}
                />
                <Input
                  type="text"
                  placeholder={t('sign_up_last_name')}
                  inputName="lastName"
                  value={registerFormik.values.lastName}
                  onChange={registerFormik.handleChange}
                  error={registerFormik.errors.lastName}
                />
                <Input
                  type="text"
                  placeholder={t('sign_in_phone')}
                  inputName="phone"
                  value={registerFormik.values.phone}
                  onChange={registerFormik.handleChange}
                  error={registerFormik.errors.phone}
                />
                <Input
                  type="text"
                  placeholder={t('sign_in_email')}
                  inputName="email"
                  value={registerFormik.values.email}
                  onChange={registerFormik.handleChange}
                  error={registerFormik.errors.email}
                />
                <Input
                  type={passwordVisible ? 'text' : 'password'}
                  placeholder={t('sign_in_password')}
                  inputName="password"
                  value={registerFormik.values.password}
                  onChange={registerFormik.handleChange}
                  error={registerFormik.errors.password}
                  icon={
                    passwordVisible ? (
                      <GoEyeClosed onClick={togglePasswordVisibility} className="cursor-pointer" />
                    ) : (
                      <IoEyeOutline onClick={togglePasswordVisibility} className="cursor-pointer" />
                    )
                  }
                />
                <Input
                  type={confirmPasswordVisible ? 'text' : 'password'}
                  placeholder={t('sign_up_confirm_password')}
                  inputName="confirmPassword"
                  value={registerFormik.values.confirmPassword}
                  onChange={registerFormik.handleChange}
                  error={registerFormik.errors.confirmPassword}
                  icon={
                    confirmPasswordVisible ? (
                      <GoEyeClosed onClick={toggleConfirmPasswordVisibility} className="cursor-pointer" />
                    ) : (
                      <IoEyeOutline onClick={toggleConfirmPasswordVisibility} className="cursor-pointer" />
                    )
                  }
                />
                <Button name={t('sign_up_create_account')} loading={loading} type="submit" />
              </form>
              <MediaButton />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Register;
